import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import toast from "react-hot-toast";

type JoinRequest = {
  id: string;
  created_at: string;
  team: { id: string; name: string; age_group: string | null } | null;
  athlete: {
    id: string;
    full_name: string | null;
    login: string;
    age_group: string | null;
    guardian: { name?: string; email?: string } | null;
  };
};

/**
 * People waiting to be let into the club, from a shared join link.
 *
 * This is the gate the whole link design rests on: until someone here says
 * yes, the sign-up bills nothing, occupies no seat and appears on no roster.
 * So the card shows itself only when there is something to decide, and says
 * how many — a queue nobody notices is the same as no gate at all.
 */
export default function JoinRequests({ clubId }: { clubId: string | null | undefined }) {
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!clubId) return;
    try {
      const { data } = await api.get<{ requests: JoinRequest[] }>(`/join-links/requests/${clubId}`);
      setRequests(data.requests || []);
    } catch {
      // Non-fatal: the rest of the page is still useful without this.
      setRequests([]);
    }
    setLoaded(true);
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: string, action: "approve" | "reject") => {
    setBusy(id);
    try {
      await api.post(`/join-links/requests/${id}/${action}`);
      toast.success(action === "approve" ? "Sportiv acceptat în echipă." : "Cerere respinsă.");
      setRequests((prev) => prev.filter((r) => r.id !== id));
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Acțiunea nu a reușit.");
      await load();
    }
    setBusy(null);
  };

  if (!loaded || requests.length === 0) return null;

  return (
    <div className="p-5 border shadow-sm bg-white border-amber-200 rounded-2xl">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[15px] font-bold text-slate-800">
          Cereri de înscriere ({requests.length})
        </h2>
        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
          Așteaptă confirmarea ta
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Au venit prin linkul de înscriere. Nu apar în echipă și nu sunt facturați până nu îi
        accepți.
      </p>

      <ul className="mt-4 space-y-2">
        {requests.map((r) => (
          <li
            key={r.id}
            className="flex flex-wrap items-center gap-3 p-3 border rounded-xl border-slate-200"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-800">
                {r.athlete.full_name || r.athlete.login}
              </p>
              <p className="text-xs text-slate-500">
                {r.team ? r.team.name : "echipă indisponibilă"}
                {r.athlete.age_group ? ` · ${r.athlete.age_group}` : ""}
                {r.athlete.guardian?.name ? ` · părinte: ${r.athlete.guardian.name}` : ""}
              </p>
              {r.athlete.guardian?.email && (
                <p className="text-xs break-all text-slate-400">{r.athlete.guardian.email}</p>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => act(r.id, "reject")}
                disabled={busy === r.id}
                className="px-3 py-1.5 text-xs font-semibold border rounded-lg border-slate-300 text-slate-600 disabled:opacity-60"
              >
                Respinge
              </button>
              <button
                type="button"
                onClick={() => act(r.id, "approve")}
                disabled={busy === r.id}
                className="px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 rounded-lg disabled:opacity-60"
              >
                Acceptă
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
